import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../admin/context/AdminContext';
import { authApi } from '../services/api';

const AuthPage = ({ mode = 'login', admin = false }) => {
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, loading, completeSession } = useAuth();
  const adminAuth = useAdmin();
  const [pendingOtp, setPendingOtp] = useState(null);
  const [forgotStep, setForgotStep] = useState('');
  const [otp, setOtp] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: admin ? 'super-admin' : 'customer'
  });
  const [error, setError] = useState('');

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    const storedAdmin = localStorage.getItem('adminUser');
    const storedUser = localStorage.getItem('fosen_user');
    const sessionUser = admin ? storedAdmin : storedUser;
    if (!sessionUser) return;
    try {
      const parsedUser = JSON.parse(sessionUser);
      if (parsedUser?.role && parsedUser.role !== 'customer') {
        navigate('/admin/dashboard', { replace: true });
      } else if (!admin && parsedUser) {
        navigate('/profile', { replace: true });
      }
    } catch (error) {
      // Ignore invalid stored session JSON; auth providers will clean up if needed.
    }
  }, [admin, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      if (isSignup && form.password !== form.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (isSignup) {
        const signupPayload = {
          name: form.fullName,
          fullName: form.fullName,
          email: form.email,
          phone: form.phone || undefined,
          password: form.password,
          confirmPassword: form.confirmPassword,
          ...(admin ? { role: form.role } : {})
        };
        const result = admin ? await adminAuth.signup(signupPayload) : await signup(signupPayload, false);
        if (result?.requiresOtp) {
          setPendingOtp({ destination: result.destination || form.email, admin });
          return;
        }
      } else {
        const loggedInUser = admin
          ? await adminAuth.login({ email: form.email, password: form.password })
          : await login({ email: form.email, password: form.password });
        if (loggedInUser?.requiresOtp) {
          setPendingOtp({ destination: loggedInUser.destination || form.email, admin });
          return;
        }
        if (!admin && loggedInUser?.role && loggedInUser.role !== 'customer') {
          localStorage.setItem('adminToken', localStorage.getItem('fosen_access_token') || '');
          localStorage.setItem('adminUser', JSON.stringify(loggedInUser));
          navigate('/admin/dashboard', { replace: true });
          return;
        }
      }
      const adminRedirect = location.state?.from?.pathname || '/admin/dashboard';
      navigate(admin ? adminRedirect : '/profile', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Authentication failed');
    }
  };

  const verifyOtpAndContinue = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await authApi.verifyOtp({ email: pendingOtp.destination, otp });
      const payload = response.data?.data;
      if (!payload?.accessToken || !payload?.user) throw new Error('Invalid OTP verification response');

      completeSession(payload);

      if (payload.user.role && payload.user.role !== 'customer') {
        localStorage.setItem('adminToken', payload.accessToken);
        localStorage.setItem('adminUser', JSON.stringify(payload.user));
        adminAuth.setUser(payload.user);
        navigate('/admin/dashboard', { replace: true });
        return;
      }

      navigate('/profile', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'OTP verification failed');
    }
  };

  const requestPasswordReset = async (event) => {
    event.preventDefault();
    setError('');
    try {
      await authApi.forgotPassword({ email: form.email });
      setForgotStep('reset');
      setPendingOtp({ destination: form.email, resetPassword: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not send reset OTP');
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      await authApi.resetPassword({ email: pendingOtp.destination, otp, password: form.password, confirmPassword: form.confirmPassword });
      setPendingOtp(null);
      setForgotStep('');
      setOtp('');
      setForm((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      setError('Password reset successful. Please login.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not reset password');
    }
  };

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="bg-white border border-[#1F2A44]/10 p-6">
          <div className="flex items-center gap-3">
            {admin ? <ShieldCheck className="text-[#E8B530]" /> : <UserRound className="text-[#E8B530]" />}
            <div>
              <p className="text-xs uppercase tracking-wider text-[#1F2A44]/60 font-semibold">{admin ? 'Admin Access' : 'Customer Account'}</p>
              <h1 className="font-serif text-2xl font-bold text-[#1F2A44]">{isSignup ? 'Create Account' : 'Login'}</h1>
            </div>
          </div>

          {forgotStep === 'request' ? (
            <form onSubmit={requestPasswordReset} className="mt-6 space-y-4">
              <p className="text-sm text-[#1F2A44]/70">Enter your account email to receive a password reset OTP.</p>
              <input value={form.email} onChange={(e) => update('email', e.target.value)} required type="email" placeholder="Email" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
              {error && <p className={`text-sm ${error.includes('successful') ? 'text-green-700' : 'text-red-600'}`}>{error}</p>}
              <button className="w-full bg-[#1F2A44] text-[#FAF7F2] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#2F3B58]">
                Send OTP
              </button>
              <button type="button" onClick={() => { setForgotStep(''); setError(''); }} className="w-full text-sm text-[#1F2A44]/70 hover:text-[#E8B530]">
                Back to login
              </button>
            </form>
          ) : forgotStep === 'reset' ? (
            <form onSubmit={resetPassword} className="mt-6 space-y-4">
              <p className="text-sm text-[#1F2A44]/70">Enter the OTP sent to {pendingOtp?.destination}, then choose a new password.</p>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder="6-digit OTP" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
              <input value={form.password} onChange={(e) => update('password', e.target.value)} required type="password" placeholder="New password" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
              <input value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} required type="password" placeholder="Confirm new password" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button className="w-full bg-[#1F2A44] text-[#FAF7F2] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#2F3B58]">
                Reset Password
              </button>
            </form>
          ) : pendingOtp ? (
            <form onSubmit={verifyOtpAndContinue} className="mt-6 space-y-4">
              <p className="text-sm text-[#1F2A44]/70">Enter the OTP sent to {pendingOtp.destination}.</p>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder="6-digit OTP" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button className="w-full bg-[#1F2A44] text-[#FAF7F2] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#2F3B58]">
                Verify OTP
              </button>
              <button type="button" onClick={() => setPendingOtp(null)} className="w-full text-sm text-[#1F2A44]/70 hover:text-[#E8B530]">
                Change email or password
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {isSignup && (
              <input value={form.fullName} onChange={(e) => update('fullName', e.target.value)} required placeholder="Full name" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
            )}
            <input value={form.email} onChange={(e) => update('email', e.target.value)} required type="email" placeholder="Email" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
            {isSignup && (
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="Phone number" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
            )}
            <input value={form.password} onChange={(e) => update('password', e.target.value)} required type="password" placeholder="Password" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />

            {admin && isSignup && (
              <>
                <select value={form.role} onChange={(e) => update('role', e.target.value)} className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8B530]">
                  <option value="super-admin">Super Admin</option>
                  <option value="product-manager">Product Manager</option>
                  <option value="order-manager">Order Manager</option>
                  <option value="delivery-manager">Delivery Manager</option>
                  <option value="support">Support Team</option>
                </select>
              </>
            )}
            {isSignup && (
              <input value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} required type="password" placeholder="Confirm password" className="w-full border border-[#1F2A44]/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]" />
            )}

            {error && <p className={`text-sm ${error.includes('successful') ? 'text-green-700' : 'text-red-600'}`}>{error}</p>}
            <button disabled={admin ? adminAuth.loading : loading} className="w-full bg-[#1F2A44] text-[#FAF7F2] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#2F3B58] disabled:opacity-60">
              {(admin ? adminAuth.loading : loading) ? 'Please wait...' : isSignup ? 'Sign Up' : 'Login'}
            </button>
            {!isSignup && (
              <button type="button" onClick={() => { setForgotStep('request'); setError(''); }} className="w-full text-sm text-[#1F2A44]/70 hover:text-[#E8B530]">
                Forgot password?
              </button>
            )}
          </form>
          )}

          {!pendingOtp && !forgotStep && <div className="mt-5 text-sm text-[#1F2A44]/70">
            {isSignup ? (
              <Link to={admin ? '/admin/login' : '/login'} className="hover:text-[#E8B530]">Already have an account? Login</Link>
            ) : admin ? null : (
              <Link to={admin ? '/admin/signup' : '/signup'} className="hover:text-[#E8B530]">Create a new account</Link>
            )}
          </div>}
        </div>
      </div>
    </main>
  );
};

export default AuthPage;
